import * as d3 from 'd3';
import { MutableRef } from 'preact/hooks';

import mapSettings from './mapSettings';

// Global map settings.
const { mapScaleMin, mapScaleMax, transitionDuration } = mapSettings;

/**
 * Zoom reset.
 *
 * @param {Object} ref  - SVG reference.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomReset( ref: MutableRef< Element | null >, zoom: d3.ZoomBehavior< Element, unknown > ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );

	svg
		.transition()
		.duration( transitionDuration )
		.call(
			zoom.transform,
			d3.zoomIdentity
		);
}

/**
 * Zoom in.
 *
 * @param {Object} ref  - SVG ref to zoom in on.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomIn( ref: MutableRef< Element | null >, zoom: d3.ZoomBehavior< Element, unknown > ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );
	const transform = d3.zoomTransform( svg.node() );
	const scale = transform.k;

	// Return early if fully zoomed in.
	if ( scale >= mapScaleMax ) {
		return;
	}

	svg
		.transition()
		.duration( transitionDuration )
		.call( zoom.scaleTo, scale * 2 );
}

/**
 * Zoom out.
 *
 * @param {Object} ref  - SVG ref to zoom out on.
 * @param {Object} zoom - d3 zoom object.
 */
export function zoomOut( ref: MutableRef< Element | null >, zoom: d3.ZoomBehavior< Element, unknown > ) {
	if ( ! ref.current ) {
		return;
	}

	const svg = d3.select( ref.current );
	const transform = d3.zoomTransform( svg.node() );
	const scale = transform.k;

	const newScale = scale / 2;

	// Return early and reset if fully zoomed out or if new scale is less than the minimum scale.
	if ( scale === mapScaleMin || newScale <= mapScaleMin ) {
		// Reset the zoom.
		zoomReset( ref, zoom );
		return;
	}

	svg
		.transition()
		.duration( transitionDuration )
		.call( zoom.scaleTo, newScale );
}
